import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useAuthContext } from "@asgardeo/auth-react";
import { useParams } from "react-router-dom";
import SearchLayout from "../../layouts/SearchLayout";

const legislationApiBaseUrl = (
  import.meta.env.VITE_LEGISLATION_API_BASE_URL
  || (import.meta.env.DEV ? "http://localhost:8200" : "https://lex.paralegal.lk")
).replace(/\/$/, "");

const legislationAppUrl = (
  import.meta.env.VITE_LEGISLATION_APP_URL || "https://lex.paralegal.lk"
).replace(/\/$/, "");

function buildMetaDescription(preview) {
  const summary = preview?.description?.trim()
    || `Legislation record for ${preview?.title || "Sri Lankan legislation"}.`;

  return `${summary} Sign in to read the complete legislation on Paralegal.`.slice(0, 320);
}

const PublicSection = ({ section, onSeeMore }) => {
  const structuralLabel = [section.chapter, section.chapter_title]
    .filter(Boolean)
    .join(" · ");
  const visibleContent = section.is_truncated
    ? section.content_preview.replace(/\.\.\.$/, "").trimEnd()
    : section.content_preview;

  return (
    <section className="border-b border-pl-border/60 py-8 last:border-b-0">
      {structuralLabel ? (
        <p className="mb-2 text-11 font-semibold uppercase tracking-[0.16em] text-pl-text-tertiary">
          {structuralLabel}
        </p>
      ) : null}
      <h3 className="mb-4 font-display text-[1.1rem] font-bold leading-snug text-pl-text-primary">
        {section.section_number}. {section.section_title}
      </h3>

      {section.amendments?.length ? (
        <div className="flex flex-wrap items-center gap-2 text-[0.8rem] text-pl-text-secondary">
          <span className="text-[0.7rem] uppercase tracking-[0.2em] text-pl-text-secondary">
            Amended by:
          </span>
          {section.amendments.map((amendment, index) => (
            <span
              key={`${amendment.ref || amendment.text || "amendment"}-${index}`}
              className="rounded border border-pl-border bg-pl-bg-subtle px-2 py-1 text-[0.75rem] font-medium text-pl-text-primary"
            >
              {amendment.ref || amendment.amendment || amendment.text || amendment.title}
            </span>
          ))}
        </div>
      ) : null}

      {section.content_preview ? (
        <p className="mt-2 font-body text-[1rem] leading-[1.65] text-pl-text-primary/90">
          {visibleContent}
          {section.is_truncated ? (
            <React.Fragment>
              ...{" "}
              <button
                type="button"
                onClick={onSeeMore}
                className="inline border-0 bg-transparent p-0 font-medium text-pl-accent underline underline-offset-2"
              >
                See more
              </button>
            </React.Fragment>
          ) : null}
        </p>
      ) : null}
    </section>
  );
};

const ReferenceSection = ({ label, items }) => {
  if (!items?.length) return null;

  return (
    <section>
      <h2 className="mb-3 text-16 font-medium text-pl-text-primary">{label}</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item, index) => (
          <div
            key={`${item.number}-${index}`}
            className="w-full rounded-[4px] border border-pl-border border-l-[3px] border-l-pl-border-strong bg-pl-bg-elevated px-4 py-3 text-left"
          >
            <span className="block text-[0.95rem] font-semibold text-pl-text-primary">
              {item.number || "—"}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

const PartPreview = ({
  part,
  onSeeMore,
  showAccessPrompt = false,
  accessPromptRef,
  legislationTitle,
  onGetAccess,
}) => {
  return (
    <section className="overflow-hidden rounded-[32px] border border-pl-border bg-pl-bg-elevated shadow-[0_20px_40px_rgba(0,0,0,0.05)]">
      <div className="bg-pl-bg-elevated px-6 py-5 text-left text-pl-text-primary">
        <div>
          <p className="text-sm uppercase tracking-[0.25em] text-pl-text-secondary">Part</p>
          <h2 className="text-lg font-semibold text-pl-text-primary">
            {part.number}{part.title ? `: ${part.title}` : ""}
          </h2>
        </div>
      </div>
      <div className="border-t border-pl-border px-6 pb-10 pt-6">
          <div className="space-y-8">
            {part.sections.map((section) => (
              <PublicSection
                key={`${section.section_number}-${section.section_title || "section"}`}
                section={section}
                onSeeMore={onSeeMore}
              />
            ))}
          </div>

          {showAccessPrompt ? (
            <React.Fragment>
              <div className="relative mt-8 overflow-hidden rounded-2xl border border-pl-border bg-pl-bg-subtle/70">
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-pl-bg/40 to-pl-bg" />
                <div className="px-6 py-6 opacity-70 blur-[3px]">
                  <h3 className="border-b border-pl-border font-display text-[1.1rem] font-bold text-pl-text-primary">
                    Continued legislation
                  </h3>
                  <p className="mt-4 font-body text-[1rem] leading-[1.65] text-pl-text-primary/90">
                    The remaining provisions continue with further sections, subsections,
                    amendment references, illustrations, explanations, and schedules.
                  </p>
                </div>
              </div>

              <div
                ref={accessPromptRef}
                className="mt-6 rounded-2xl border border-pl-border bg-pl-bg-subtle px-6 py-6 md:px-8"
              >
                <h3 className="text-base font-semibold text-pl-text-primary">
                  Sign in or sign up for free for full access
                </h3>
                <p className="mt-3 text-base leading-8 text-pl-text-secondary md:text-[17px]">
                  Read the complete {legislationTitle}, and all other primary statutes at paralegal.lk legislation library.
                </p>
                <button
                  type="button"
                  onClick={onGetAccess}
                  className="mt-4 rounded-pl border border-pl-accent px-4 py-2 text-sm font-medium text-pl-accent transition-colors duration-150 hover:bg-pl-accent hover:text-white"
                >
                  Get access
                </button>
              </div>
            </React.Fragment>
          ) : null}
      </div>
    </section>
  );
};

const LegislationPreview = () => {
  const { legislationName = "" } = useParams();
  const { signIn, state } = useAuthContext();
  const [preview, setPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [showIntroDetails, setShowIntroDetails] = useState(false);
  const ctaRef = useRef(null);

  const canonicalPath = `/legislations/${encodeURIComponent(legislationName)}`;
  const fullReaderUrl = `${legislationAppUrl}/${encodeURIComponent(legislationName)}`;

  useEffect(() => {
    if (state.isAuthenticated && !state.isLoading) {
      window.location.assign(fullReaderUrl);
    }
  }, [fullReaderUrl, state.isAuthenticated, state.isLoading]);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setLoadError("");

    fetch(
      `${legislationApiBaseUrl}/api/legislations/public/${encodeURIComponent(legislationName)}`,
      { signal: controller.signal }
    )
      .then(async (response) => {
        if (response.status === 404) {
          throw new Error("No legislation was found for this identifier.");
        }
        if (response.status === 429) {
          throw new Error("Too many requests. Please try again shortly.");
        }
        if (!response.ok) {
          throw new Error("The legislation preview could not be loaded right now.");
        }
        return response.json();
      })
      .then(setPreview)
      .catch((error) => {
        if (error.name === "AbortError") return;
        setPreview(null);
        setLoadError(error.message || "The legislation preview could not be loaded.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [legislationName]);

  const metadata = preview?.metadata || {};
  const partGroups = [];
  for (const section of preview?.sections || []) {
    const partNumber = section.part || "Main Part";
    let part = partGroups.find((item) => item.number === partNumber);
    if (!part) {
      part = { number: partNumber, title: section.part_title || "", sections: [] };
      partGroups.push(part);
    }
    part.sections.push(section);
  }
  const pageTitle = preview
    ? `${preview.title} | Sri Lankan Legislation | Paralegal`
    : "Sri Lankan Legislation | Paralegal";
  const metaDescription = useMemo(() => buildMetaDescription(preview), [preview]);

  const handleGetAccess = () => {
    sessionStorage.removeItem("redirectToDocument");
    sessionStorage.removeItem("urlString");
    sessionStorage.setItem("redirectAfterLogin", canonicalPath);
    signIn();
  };

  const structuredData = preview ? {
    "@context": "https://schema.org",
    "@type": "Legislation",
    name: preview.title,
    identifier: preview.id,
    description: preview.description || undefined,
    datePublished: metadata.enactment_date || undefined,
    legislationDate: metadata.enactment_date || undefined,
    url: `https://www.paralegal.lk${canonicalPath}`,
  } : null;

  return (
    <div className="relative">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="robots" content={loadError ? "noindex,nofollow" : "index,follow"} />
        <link rel="canonical" href={`https://www.paralegal.lk${canonicalPath}`} />
        {structuredData ? (
          <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        ) : null}
      </Helmet>

      <SearchLayout isResults={true} showTab={false} showSearch={false}>
        <section className="flex w-full max-w-5xl flex-col px-6 pb-20 pt-[120px] md:px-20 md:pt-[120px]">
          {isLoading ? (
            <div className="rounded-2xl border border-pl-border bg-pl-bg-elevated p-8">
              <p className="text-16 text-pl-text-secondary">Loading legislation…</p>
            </div>
          ) : loadError ? (
            <div className="rounded-2xl border border-pl-border bg-pl-bg-elevated p-8">
              <h1 className="text-2xl font-display font-semibold text-pl-text-primary">
                Legislation not found
              </h1>
              <p className="mt-3 text-pl-text-secondary">{loadError}</p>
            </div>
          ) : (
            <div className="space-y-10 text-pl-text-primary">
              <div className="rounded-2xl border border-pl-border bg-pl-bg-elevated p-6 shadow-pl-sm">
                <header className="mb-8 border-b border-pl-border pb-8">
                  <p className="mb-3 text-12 font-semibold uppercase tracking-[0.2em] text-pl-text-tertiary">
                    Sri Lankan Legislation
                  </p>
                  <h1 className="font-display text-[2.5rem] font-bold leading-tight tracking-tight text-pl-text-primary">
                    {preview.title}
                  </h1>
                  {preview.description ? (
                    <p className="mt-4 max-w-3xl font-display text-lg italic text-pl-text-primary/80">
                      {preview.description}
                    </p>
                  ) : null}
                  {metadata.enactment_year ? (
                    <p className="mt-3 text-sm text-pl-text-secondary">
                      Enacted in {metadata.enactment_year}
                    </p>
                  ) : null}
                </header>

                {(metadata.enactment_date
                  || preview.numbers?.act_numbers?.length
                  || preview.numbers?.law_numbers?.length
                  || preview.numbers?.ordinance_numbers?.length) ? (
                  <React.Fragment>
                    <button
                      type="button"
                      onClick={() => setShowIntroDetails((current) => !current)}
                      className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-pl-accent hover:underline focus:outline-none"
                    >
                      <span>{showIntroDetails ? "▼" : "▶"}</span>
                      {showIntroDetails ? "Hide details" : "See more details"}
                    </button>

                    {showIntroDetails ? (
                      <div className="space-y-6">
                        {metadata.enactment_date ? (
                          <div>
                            <h2 className="mb-2 text-16 font-medium text-pl-text-primary">
                              Enactment Date
                            </h2>
                            <p className="text-pl-text-secondary">{metadata.enactment_date}</p>
                          </div>
                        ) : null}
                        <ReferenceSection label="Act Numbers" items={preview.numbers?.act_numbers} />
                        <ReferenceSection label="Law Numbers" items={preview.numbers?.law_numbers} />
                        <ReferenceSection
                          label="Ordinance Numbers"
                          items={preview.numbers?.ordinance_numbers}
                        />
                      </div>
                    ) : null}
                  </React.Fragment>
                ) : null}
              </div>

              {partGroups.length ? (
                <div className="space-y-8">
                  {partGroups.map((part, index) => (
                    <PartPreview
                      key={part.number}
                      part={part}
                      onSeeMore={() => ctaRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      })}
                      showAccessPrompt={index === partGroups.length - 1}
                      accessPromptRef={ctaRef}
                      legislationTitle={preview.title}
                      onGetAccess={handleGetAccess}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </section>
      </SearchLayout>
    </div>
  );
};

export default LegislationPreview;
